import React from 'react';
import { motion } from 'framer-motion';
import { Target, Boxes, ShieldCheck, RefreshCw } from 'lucide-react';
import Card from '../ui/Card';

const credibilityPoints = [
  {
    icon: <Target className="w-6 h-6 text-brand-600 dark:text-brand-400" />,
    title: 'Practical Innovation',
    description: 'Technology designed around real-world problems. We prioritize verifiable utility and immediate operational value over speculative hype.'
  },
  {
    icon: <Boxes className="w-6 h-6 text-indigoBrand-600 dark:text-indigoBrand-400" />,
    title: 'Product Engineering',
    description: 'From architecture to deployment. We maintain end-to-end engineering discipline across API protocols, containerization, and distributed scalability.'
  },
  {
    icon: <ShieldCheck className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />,
    title: 'Security by Design',
    description: 'Security and privacy considered throughout development. Data minimization, zero-trust perimeter defense, and strict encryption at rest and in transit.'
  },
  {
    icon: <RefreshCw className="w-6 h-6 text-amber-600 dark:text-amber-400" />,
    title: 'Continuous Innovation',
    description: 'Researching emerging technologies and practical applications. Continuous evaluation of neural models, media forensics, and telematics systems.'
  }
];

const Credibility = () => {
  return (
    <section className="py-24 relative bg-white dark:bg-slate-950 border-t border-slate-200/80 dark:border-slate-800">
      <div className="container mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold uppercase tracking-wider mb-4"
          >
            Our Principles
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.05 }}
            className="text-3xl md:text-5xl font-display font-bold text-slate-900 dark:text-white mb-4 tracking-tight"
          >
            Why Mira Future Tech Vision
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed"
          >
            We adhere to rigorous engineering standards that make our technology trustworthy, resilient, and ready for real-world enterprise adoption.
          </motion.p>
        </div>

        {/* 4 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {credibilityPoints.map((item, idx) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.08 }}
            >
              <Card className="h-full border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-7 shadow-subtle hover:shadow-card transition-all duration-300">
                <div className="w-12 h-12 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 flex items-center justify-center mb-6">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold font-display text-slate-900 dark:text-white mb-3">
                  {item.title}
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  {item.description}
                </p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Credibility;
